import React, { useRef, useState } from 'react';
import { Alert, Button, Descriptions, Input, Modal, Space, Tag, message } from 'antd';
import {
  CheckCircleOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { useImportTradingDiary, type TradingDiaryImportPayload } from '@/api/tradingDiary';

const { TextArea } = Input;

interface ImportBundle {
  station: string;
  date: string;
  note: {
    pre_judgement: string;
    intraday_action: string;
    deviation_reason: string;
    self_score: number;
  };
  review_strategy: TradingDiaryImportPayload['review_strategy'];
}

interface TradingDiaryImportModalProps {
  open: boolean;
  station: string;
  date: string;
  onClose: () => void;
  onImported: () => void;
}

const IMPORT_ACTION_TYPES = new Set([
  'charge',
  'declared_charge',
  'intraday_charge',
  'ust_charge',
  'curtailment_charge',
  'discharge',
  'declared_discharge',
  'carryover_discharge',
  'no_operation',
]);

const MIRROR_KEYWORDS = [
  '策略无需修正',
  '策略无须修正',
  '无需调整',
  '沿用实际申报',
  '参考实际申报',
  '实际申报策略',
];

export function parseTradingDiaryImportText(text: string): ImportBundle | null {
  if (!text || !text.trim()) return null;

  const trimmed = text.trim();
  try {
    const obj = JSON.parse(trimmed);
    if (_validateBundle(obj)) return obj as ImportBundle;
  } catch {
    // Markdown input is handled below.
  }

  const matches = [...trimmed.matchAll(/```json\s*([\s\S]*?)```/gi)];
  if (matches.length === 0) return null;

  const lastMatch = matches[matches.length - 1];
  try {
    const obj = JSON.parse(lastMatch[1]);
    if (_validateBundle(obj)) return obj as ImportBundle;
  } catch {
    return null;
  }

  return null;
}

function _validateBundle(obj: unknown): obj is ImportBundle {
  if (!obj || typeof obj !== 'object') return false;
  const b = obj as Record<string, unknown>;
  if (typeof b.station !== 'string' || !b.station.trim()) return false;
  if (typeof b.date !== 'string' || !_isValidIsoDate(b.date)) return false;
  if (!b.note || typeof b.note !== 'object') return false;
  if (!b.review_strategy || typeof b.review_strategy !== 'object') return false;

  const note = b.note as Record<string, unknown>;
  if (typeof note.pre_judgement !== 'string' || !note.pre_judgement.trim()) return false;
  if (typeof note.intraday_action !== 'string' || !note.intraday_action.trim()) return false;
  if (typeof note.deviation_reason !== 'string' || !note.deviation_reason.trim()) return false;
  if (!Number.isInteger(note.self_score) || (note.self_score as number) < 0 || (note.self_score as number) > 100) return false;

  const rs = b.review_strategy as Record<string, unknown>;
  if (rs.strategy_type !== 'ex_ante_review') return false;
  if (typeof rs.strategy_name !== 'string' || !rs.strategy_name.trim()) return false;
  if (typeof rs.raw_text !== 'string' || !rs.raw_text.trim()) return false;
  if (!Array.isArray(rs.actions)) return false;
  if (!Array.isArray(rs.uncertainties) || rs.uncertainties.some((item) => typeof item !== 'string')) return false;
  if (rs.status !== 'draft') return false;
  if (!rs.source || typeof rs.source !== 'object' || Array.isArray(rs.source)) return false;
  if (!_isOptionalFiniteNumber(rs.initial_soc_mwh)) return false;
  if (!_isOptionalFiniteNumber(rs.initial_soc_percent)) return false;
  if (typeof rs.initial_soc_mwh === 'number' && rs.initial_soc_mwh < 0) return false;
  if (
    typeof rs.initial_soc_percent === 'number'
    && (rs.initial_soc_percent < 0 || rs.initial_soc_percent > 100)
  ) return false;
  if (rs.initial_soc_mwh == null && rs.initial_soc_percent == null) return false;

  const marker = `${rs.strategy_name} ${rs.raw_text}`;
  if (rs.actions.length === 0 && !MIRROR_KEYWORDS.some((keyword) => marker.includes(keyword))) return false;
  if (rs.actions.some((action) => {
    if (!action || typeof action !== 'object' || Array.isArray(action)) return true;
    const actionType = (action as Record<string, unknown>).type;
    return typeof actionType !== 'string' || !IMPORT_ACTION_TYPES.has(actionType);
  })) return false;
  return true;
}

function _isOptionalFiniteNumber(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'number' && Number.isFinite(value));
}

function _isValidIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

const TradingDiaryImportModal: React.FC<TradingDiaryImportModalProps> = ({
  open,
  station,
  date,
  onClose,
  onImported,
}) => {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ImportBundle | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportTradingDiary();

  const reset = () => {
    setText('');
    setParsed(null);
    setParseError(null);
    setMismatch(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTextChange = (value: string) => {
    setText(value);
    setParsed(null);
    setParseError(null);
    setMismatch(null);
  };

  const handleParse = () => {
    setParseError(null);
    setMismatch(null);
    setParsed(null);

    const result = parseTradingDiaryImportText(text);
    if (!result) {
      setParseError('解析失败：请检查场站、日期、日志字段和事前复盘策略是否完整');
      return;
    }

    if (result.station && result.station !== station) {
      setMismatch(`场站不一致：导入包为 ${result.station}，当前选择为 ${station}`);
      return;
    }
    if (result.date && result.date !== date) {
      setMismatch(`日期不一致：导入包为 ${result.date}，当前选择为 ${date}`);
      return;
    }

    setParsed(result);
  };

  const handleFileSelect = () => {
    const input = fileRef.current;
    if (!input) return;
    const file = input.files?.[0];
    if (!file) return;
    file.text().then((content) => {
      handleTextChange(content);
      input.value = '';
    }).catch(() => {
      message.error('文件读取失败');
    });
  };

  const handleConfirm = async () => {
    if (!parsed) return;
    const payload: TradingDiaryImportPayload = {
      station,
      date,
      note: {
        pre_judgement: parsed.note.pre_judgement || '',
        intraday_action: parsed.note.intraday_action || '',
        deviation_reason: parsed.note.deviation_reason || '',
        self_score: parsed.note.self_score,
      },
      review_strategy: {
        strategy_type: 'ex_ante_review',
        strategy_name: (parsed.review_strategy.strategy_name as string) || undefined,
        initial_soc_mwh: (parsed.review_strategy.initial_soc_mwh as number) ?? undefined,
        initial_soc_percent: (parsed.review_strategy.initial_soc_percent as number) ?? undefined,
        actions: (parsed.review_strategy.actions as Array<Record<string, unknown>>) || [],
        carryover_soc: parsed.review_strategy.carryover_soc ?? null,
        raw_text: (parsed.review_strategy.raw_text as string) || '',
        uncertainties: (parsed.review_strategy.uncertainties as string[]) || [],
        status: (parsed.review_strategy.status as string) || 'draft',
        source: parsed.review_strategy.source || {},
      },
    };

    try {
      await importMutation.mutateAsync(payload);
      message.success('复盘导入成功');
      reset();
      onImported();
    } catch {
      message.error('复盘导入失败');
    }
  };

  const previewFields = parsed ? [
    { label: '日前判断', children: (parsed.note.pre_judgement || '-').substring(0, 60) },
    { label: '自评分', children: parsed.note.self_score ?? '-' },
    { label: '策略类型', children: '事前复盘' },
    { label: '策略名称', children: String(parsed.review_strategy.strategy_name || '-') },
    {
      label: '期初 SOC',
      children: parsed.review_strategy.initial_soc_mwh != null
        ? `${parsed.review_strategy.initial_soc_mwh} MWh`
        : parsed.review_strategy.initial_soc_percent != null
          ? `${parsed.review_strategy.initial_soc_percent}%（服务器换算）`
          : '-',
    },
    {
      label: '策略动作',
      children: Array.isArray(parsed.review_strategy.actions)
        ? `${parsed.review_strategy.actions.length} 条`
        : '0 条',
    },
  ] : [];

  return (
    <Modal
      title={
        <Space wrap size={8}>
          <ImportOutlined />
          <span>导入复盘</span>
          <Tag color="cyan">{station}</Tag>
          <Tag>{date}</Tag>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={620}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <TextArea
          rows={10}
          placeholder="粘贴 Codex 生成的 JSON，或包含 JSON 代码块的 Markdown"
          value={text}
          onChange={(event) => handleTextChange(event.target.value)}
        />

        <Space wrap>
          <Button icon={<FileSearchOutlined />} onClick={handleParse} disabled={!text.trim()}>
            解析预览
          </Button>
          <Button icon={<FileTextOutlined />} onClick={() => { fileRef.current?.click(); }}>
            选择文件
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".md,.markdown,.json,text/markdown,application/json"
            aria-label="选择复盘导入文件"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
        </Space>

        {parseError && (
          <Alert type="error" message={parseError} showIcon />
        )}

        {mismatch && (
          <Alert type="warning" message={mismatch} showIcon />
        )}

        {parsed && !mismatch && (
          <>
            <Descriptions column={1} size="small" bordered items={previewFields} />
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleConfirm}
              loading={importMutation.isPending}
              block
            >
              确认导入
            </Button>
          </>
        )}
      </Space>
    </Modal>
  );
};

export default TradingDiaryImportModal;
